import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Phone, Inbox, GripVertical, AlertTriangle } from '../../icons/wp-icons';
import { getPoolByBearbeiter, updateAnfrage } from '../../api/anfrageApi';
import { useAnfragenRefresh } from '../../hooks/useDataRefresh';
import AnfrageHoverPreview from '../AnfrageHoverPreview';
import toast from 'react-hot-toast';

// Lucas/Husmann&Dreier-Feedback 2026-05-07: Disponenten/Chef wollen auf
// einen Blick sehen, wer welche offenen Anrufe hat. Kanban-Style mit
// Spalte pro Mitarbeiter (auch leere Spalten als Drag&Drop-Ziele) +
// HTML5-natives Drag&Drop um Anrufe zwischen Mitarbeitern zu schieben
// UND um die Spalten-Reihenfolge selbst zu verschieben (per Header-Grip).
// Reihenfolge wird per User in localStorage persistiert.

const COLUMN_ORDER_KEY = 'anrufe-pool-column-order-v1';

const PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
];

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFor(seed) {
  if (!seed) return PALETTE[0];
  let hash = 0;
  const str = String(seed);
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function formatRelTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `vor ${diffD} T.`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function statusLabel(status) {
  if (status === 'abgeschlossen') return { label: 'Erledigt', color: 'bg-green-100 text-green-700' };
  if (status === 'in_bearbeitung') return { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700' };
  return { label: 'Offen', color: 'bg-blue-100 text-blue-700' };
}

const colKey = (bearbeiterId) => bearbeiterId || 'unassigned';

export default function AnrufePoolWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ columns: [], totalOffen: 0 });
  const [error, setError] = useState(null);

  // Anfrage-Drag-State (Anrufe zwischen Mitarbeitern schieben)
  const [draggingAnfrageId, setDraggingAnfrageId] = useState(null);
  const [anfrageDropTarget, setAnfrageDropTarget] = useState(null);

  // Column-Drag-State (Spalten-Reihenfolge ändern, persistiert via localStorage)
  const [draggingColumnKey, setDraggingColumnKey] = useState(null);
  const [columnDropTarget, setColumnDropTarget] = useState(null);
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(COLUMN_ORDER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const load = useCallback(async () => {
    try {
      const res = await getPoolByBearbeiter();
      setData(res);
      setError(null);
    } catch (e) {
      console.error('[AnrufePoolWidget] Laden fehlgeschlagen:', e);
      setError('Pool konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAnfragenRefresh(load);

  // Spalten in user-definierter Reihenfolge — neue Mitarbeiter (die noch
  // nicht in der gespeicherten Order sind) hängen wir ans Ende an.
  const orderedColumns = useMemo(() => {
    if (!data.columns) return [];
    if (!columnOrder || !Array.isArray(columnOrder)) return data.columns;
    const map = new Map(data.columns.map(c => [colKey(c.bearbeiterId), c]));
    const ordered = [];
    for (const key of columnOrder) {
      if (map.has(key)) {
        ordered.push(map.get(key));
        map.delete(key);
      }
    }
    for (const remaining of map.values()) {
      ordered.push(remaining);
    }
    return ordered;
  }, [data.columns, columnOrder]);

  // ── Anfrage-Drag (Anruf einem Mitarbeiter zuordnen) ────────────────────────

  const handleAnfrageDragStart = useCallback((e, anfrageId, fromBearbeiterId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-anfrage-id', anfrageId);
    e.dataTransfer.setData('application/x-from-bearbeiter', fromBearbeiterId || '');
    setDraggingAnfrageId(anfrageId);
  }, []);

  const handleAnfrageDragEnd = useCallback(() => {
    setDraggingAnfrageId(null);
    setAnfrageDropTarget(null);
  }, []);

  // ── Column-Drag (Spalten-Reihenfolge ändern) ───────────────────────────────

  const handleColumnDragStart = useCallback((e, columnKey) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-pool-column-key', columnKey);
    setDraggingColumnKey(columnKey);
  }, []);

  const handleColumnDragEnd = useCallback(() => {
    setDraggingColumnKey(null);
    setColumnDropTarget(null);
  }, []);

  // ── Gemeinsame DragOver/Drop-Handler — Type-Detection ──────────────────────

  const handleColumnContainerDragOver = useCallback((e, targetBearbeiterId) => {
    e.preventDefault();
    const types = e.dataTransfer.types;
    if (types.includes('application/x-pool-column-key')) {
      e.dataTransfer.dropEffect = 'move';
      setColumnDropTarget(colKey(targetBearbeiterId));
    } else {
      e.dataTransfer.dropEffect = 'move';
      setAnfrageDropTarget(colKey(targetBearbeiterId));
    }
  }, []);

  const handleColumnContainerDragLeave = useCallback(() => {
    setAnfrageDropTarget(null);
    setColumnDropTarget(null);
  }, []);

  const handleColumnContainerDrop = useCallback(async (e, targetBearbeiterId, targetName) => {
    e.preventDefault();
    const types = e.dataTransfer.types;
    setAnfrageDropTarget(null);
    setColumnDropTarget(null);

    // 1) Spalten-Reorder hat Priorität
    if (types.includes('application/x-pool-column-key')) {
      const sourceKey = e.dataTransfer.getData('application/x-pool-column-key');
      const targetKey = colKey(targetBearbeiterId);
      setDraggingColumnKey(null);
      if (!sourceKey || sourceKey === targetKey) return;

      const currentKeys = orderedColumns.map(c => colKey(c.bearbeiterId));
      const filtered = currentKeys.filter(k => k !== sourceKey);
      const targetIdx = filtered.indexOf(targetKey);
      if (targetIdx < 0) return;
      filtered.splice(targetIdx, 0, sourceKey);
      try {
        localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(filtered));
      } catch (e) { /* ignore quota errors */ }
      setColumnOrder(filtered);
      return;
    }

    // 2) Anfrage-Drop — bestehende Logik
    const anfrageId = e.dataTransfer.getData('application/x-anfrage-id');
    const fromBearbeiterId = e.dataTransfer.getData('application/x-from-bearbeiter') || null;
    setDraggingAnfrageId(null);
    if (!anfrageId) return;
    if ((fromBearbeiterId || null) === (targetBearbeiterId || null)) return;

    // Optimistic Update
    const snapshot = data;
    setData(prev => {
      const cols = prev.columns.map(col => ({ ...col, offeneAnrufe: [...col.offeneAnrufe] }));
      let movedAnfrage = null;
      for (const col of cols) {
        const idx = col.offeneAnrufe.findIndex(a => a.id === anfrageId);
        if (idx >= 0) {
          movedAnfrage = col.offeneAnrufe[idx];
          col.offeneAnrufe.splice(idx, 1);
          col.totalCount = Math.max(0, col.totalCount - 1);
          break;
        }
      }
      if (!movedAnfrage) return prev;
      const targetCol = cols.find(c => (c.bearbeiterId || null) === (targetBearbeiterId || null));
      if (targetCol) {
        targetCol.offeneAnrufe.unshift({ ...movedAnfrage, bearbeiterId: targetBearbeiterId || null });
        targetCol.totalCount += 1;
      }
      return { ...prev, columns: cols };
    });

    try {
      await updateAnfrage(anfrageId, { bearbeiterId: targetBearbeiterId || null });
      const targetLabel = targetBearbeiterId ? (targetName || 'Mitarbeiter') : 'Nicht zugewiesen';
      toast.success(`Anruf zugewiesen an ${targetLabel}`);
      load();
    } catch (err) {
      console.error('[AnrufePoolWidget] Zuweisung fehlgeschlagen:', err);
      toast.error('Zuweisung konnte nicht gespeichert werden');
      setData(snapshot);
    }
  }, [data, orderedColumns, load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-red-600">{error}</div>
    );
  }
  if (!orderedColumns || orderedColumns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-text-muted">
        <Inbox className="w-10 h-10 mb-2 opacity-30" />
        <p className="text-sm">Keine Mitarbeiter angelegt</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 pt-2 pb-1">
        <p className="text-xs text-text-muted">
          Anrufe ziehen → Mitarbeiter zuweisen. Spalten-Header ziehen → Reihenfolge ändern.
        </p>
      </div>
      <div className="flex-1 overflow-x-auto overflow-y-hidden px-4 pb-3">
        <div className="flex gap-3 h-full" style={{ minWidth: 'min-content' }}>
          {orderedColumns.map(col => {
            const isUnassigned = !col.bearbeiterId;
            const colorClass = isUnassigned
              ? 'bg-gray-100 text-gray-500'
              : colorFor(col.bearbeiterId);
            const initials = isUnassigned ? '?' : getInitials(col.name);
            const key = colKey(col.bearbeiterId);
            const isAnfrageDropTarget = anfrageDropTarget === key;
            const isColumnDropTarget = columnDropTarget === key && draggingColumnKey && draggingColumnKey !== key;
            const isBeingDragged = draggingColumnKey === key;
            return (
              <div
                key={key}
                onDragOver={(e) => handleColumnContainerDragOver(e, col.bearbeiterId)}
                onDragLeave={handleColumnContainerDragLeave}
                onDrop={(e) => handleColumnContainerDrop(e, col.bearbeiterId, col.name)}
                className={`flex-shrink-0 w-64 border rounded-lg flex flex-col h-full transition ${
                  isColumnDropTarget
                    ? 'bg-purple-50 border-purple-400 border-2'
                    : isAnfrageDropTarget
                      ? 'bg-primary/5 border-primary border-2'
                      : 'bg-gray-50 border-gray-200'
                } ${isBeingDragged ? 'opacity-40' : ''}`}
              >
                {/* Header — DnD-Handle für Spalten-Reorder. Klar erkennbar
                    durch GripVertical + cursor-grab. Anfragen darunter
                    bleiben separat draggable. */}
                <div
                  draggable
                  onDragStart={(e) => handleColumnDragStart(e, key)}
                  onDragEnd={handleColumnDragEnd}
                  className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 bg-white rounded-t-lg cursor-grab active:cursor-grabbing select-none"
                  title="Ziehen, um Spalten-Reihenfolge zu ändern"
                >
                  <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span
                    className={`rounded-full flex items-center justify-center font-semibold text-xs ${colorClass}`}
                    style={{ width: '24px', height: '24px' }}
                  >
                    {initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text truncate">{col.name}</p>
                  </div>
                  <span className="text-xs font-medium text-text-muted bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {col.totalCount}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {col.offeneAnrufe.length === 0 ? (
                    <p className="text-xs text-text-muted text-center py-6 italic">
                      {isAnfrageDropTarget ? 'Hier ablegen' : 'Kein offener Anruf'}
                    </p>
                  ) : (
                    col.offeneAnrufe.map(a => {
                      const sc = statusLabel(a.status);
                      const isDragging = draggingAnfrageId === a.id;
                      // Notruf-Betreff ohne den Server-Prefix "🚨 NOTFALL — "
                      // darstellen — das Badge zeigt den Notruf-Status, der
                      // Prefix wäre redundant.
                      const displayBetreff = (a.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || '—';
                      return (
                        <AnfrageHoverPreview key={a.id} anfrage={a}>
                          <div
                            draggable
                            onDragStart={(e) => handleAnfrageDragStart(e, a.id, col.bearbeiterId)}
                            onDragEnd={handleAnfrageDragEnd}
                            className={`bg-white border rounded-md p-2 hover:shadow-sm transition cursor-grab active:cursor-grabbing ${
                              a.istNotfall
                                ? 'border-red-300 hover:border-red-500 bg-red-50/40'
                                : 'border-gray-200 hover:border-primary'
                            } ${isDragging ? 'opacity-40' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="min-w-0 flex-1 flex items-center gap-1.5">
                                {a.istNotfall && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-600 text-white rounded shrink-0"
                                    title="Notdienst — Anruf wurde an den Notdienst weitergeleitet"
                                  >
                                    <AlertTriangle className="w-2.5 h-2.5" />
                                    Notdienst
                                  </span>
                                )}
                                <Link
                                  to={`/anfragen/${a.id}`}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className={`text-sm font-medium truncate hover:text-primary ${
                                    a.istNotfall ? 'text-red-900' : 'text-text'
                                  }`}
                                >
                                  {displayBetreff}
                                </Link>
                              </div>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${sc.color}`}>
                                {sc.label}
                              </span>
                            </div>
                            {a.kunde && (
                              <p className="text-xs text-text-muted truncate flex items-center gap-1">
                                <Phone className="w-3 h-3 shrink-0" />
                                {a.kunde.name}
                              </p>
                            )}
                            <p className="text-[11px] text-gray-400 mt-1">{formatRelTime(a.erstelltAm)}</p>
                          </div>
                        </AnfrageHoverPreview>
                      );
                    })
                  )}
                  {col.totalCount > col.offeneAnrufe.length && (
                    <p className="text-[11px] text-text-muted text-center pt-1">
                      +{col.totalCount - col.offeneAnrufe.length} weitere
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
